import { useEffect, useState } from 'preact/hooks';
import {
  platformOrgs,
  OrgListItem,
  formatBytes,
  formatInt,
} from './adminApi';
import { StepUpModal, StepUpRequiredError, runWithStepUp } from './StepUpModal';

interface Props {
  onOpen(orgId: string): void;
}

export function OrgsListPage({ onOpen }: Props) {
  const [rows, setRows] = useState<OrgListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('created_at_desc');
  const [offset, setOffset] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const pageSize = 50;

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const resp = await platformOrgs.list({
        search: search.trim() || undefined,
        sort,
        limit: pageSize,
        offset,
      });
      setRows(resp.orgs || []);
      setTotal(Number(resp.total));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, offset]);

  function onSubmit(e: Event) {
    e.preventDefault();
    setOffset(0);
    load();
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ marginTop: 0 }}>Organizations</h1>
          <p style={{ color: 'var(--admin-text-dim)' }}>
            All tenant organizations. Click a row to drill into events, members,
            gateway, storage, notifications, audit, and pipeline health.
          </p>
        </div>
        <button class="primary" onClick={() => setShowCreate(true)}>
          + New Organization
        </button>
      </div>

      <form onSubmit={onSubmit} style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <input
          placeholder="Search by name, slug, short ID, or owner email"
          value={search}
          onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
          style={{ flex: 1 }}
        />
        <select
          value={sort}
          onChange={(e) => {
            setSort((e.target as HTMLSelectElement).value);
            setOffset(0);
          }}
          style={{ width: 200 }}
        >
          <option value="created_at_desc">Newest</option>
          <option value="name_asc">Name (A→Z)</option>
          <option value="member_count_desc">Most members</option>
          <option value="storage_bytes_desc">Largest storage</option>
        </select>
        <button class="primary" type="submit">
          Search
        </button>
      </form>

      {showCreate && (
        <CreateOrgModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            setOffset(0);
            load();
          }}
        />
      )}

      {err && <div class="error-banner">{err}</div>}

      <table class="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Slug</th>
            <th>Owner</th>
            <th style={{ textAlign: 'right' }}>Members</th>
            <th style={{ textAlign: 'right' }}>Events</th>
            <th style={{ textAlign: 'right' }}>Media</th>
            <th style={{ textAlign: 'right' }}>Storage</th>
            <th>Tier</th>
            <th>Status</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              onClick={() => onOpen(r.id)}
              style={{ cursor: 'pointer' }}
            >
              <td>
                <strong>{r.name}</strong>
              </td>
              <td class="mono">{r.slug}</td>
              <td class="mono">{r.ownerEmail || '—'}</td>
              <td style={{ textAlign: 'right' }}>{formatInt(r.memberCount)}</td>
              <td style={{ textAlign: 'right' }}>{formatInt(r.eventCount)}</td>
              <td style={{ textAlign: 'right' }}>{formatInt(r.mediaCount)}</td>
              <td style={{ textAlign: 'right' }}>{formatBytes(r.storageBytes)}</td>
              <td class="mono">{r.subscriptionTier}</td>
              <td>
                <span class={`badge ${r.subscriptionStatus === 'active' ? 'success' : 'failure'}`}>
                  {r.subscriptionStatus}
                </span>
              </td>
              <td class="mono">{new Date(r.createdAt).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {!loading && rows.length === 0 && (
        <p style={{ color: 'var(--admin-text-dim)' }}>No organizations match.</p>
      )}

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: 'var(--admin-text-dim)', fontSize: 13 }}>
          {total > 0
            ? `Showing ${offset + 1}–${Math.min(offset + pageSize, total)} of ${total}`
            : 'No rows'}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - pageSize))}>
            ← Prev
          </button>
          <button
            disabled={offset + pageSize >= total || loading}
            onClick={() => setOffset(offset + pageSize)}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}

// CreateOrgModal — admin form for provisioning a new tenant organization.
//
// Submits to PlatformOrgService.CreateOrganization, which creates org +
// owner user + membership in a transaction, then emails a 7-day claim
// link. The success view shows the claim URL so the admin can copy it
// if the email fails to deliver.
function CreateOrgModal({ onClose, onCreated }: { onClose(): void; onCreated(): void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [welcomeNote, setWelcomeNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<{ orgId: string; claimUrl: string; claimExpiresAt: string } | null>(null);
  // Step-up retry. Set to a closure that re-runs submit() after the
  // modal grants the 5-minute step-up window. Same pattern as
  // BillingPage / OrgBillingTab.
  const [stepUpRetry, setStepUpRetry] = useState<(() => void) | null>(null);

  // Slug auto-fill from name on first edit. Lowercase, dashes for spaces,
  // strip anything outside [a-z0-9-]. Once the user has typed in the slug
  // field directly we stop overwriting (tracked via slugTouched).
  const [slugTouched, setSlugTouched] = useState(false);
  function deriveSlug(s: string): string {
    return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  async function runCreate() {
    setErr(null);
    setSubmitting(true);
    try {
      const resp = await runWithStepUp(() =>
        platformOrgs.create({
          name: name.trim(),
          slug: slug.trim(),
          ownerEmail: ownerEmail.trim(),
          ownerName: ownerName.trim(),
          welcomeNote: welcomeNote.trim() || undefined,
        }),
      );
      setResult({
        orgId: resp.orgId,
        claimUrl: resp.claimUrl,
        claimExpiresAt: resp.claimExpiresAt,
      });
    } catch (e2) {
      if (e2 instanceof StepUpRequiredError) {
        // Capture the same form state in a retry closure; the modal
        // will invoke it after a successful re-auth.
        setStepUpRetry(() => () => runCreate());
      } else {
        setErr(e2 instanceof Error ? e2.message : 'Create failed');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function submit(e: Event) {
    e.preventDefault();
    if (submitting) return;
    await runCreate();
  }

  function copyClaimURL() {
    if (result) {
      navigator.clipboard.writeText(result.claimUrl).catch(() => {
        /* clipboard may be blocked in older browsers — admin can copy manually */
      });
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--admin-bg, #fff)', padding: 24, borderRadius: 8,
          width: 'min(560px, 92vw)', maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        {result ? (
          <>
            <h2 style={{ marginTop: 0 }}>Organization created</h2>
            <p>Claim email sent to <strong>{ownerEmail}</strong>. The owner has 7 days to set a password and claim the account.</p>
            <p style={{ color: 'var(--admin-text-dim)', fontSize: 13 }}>
              If the email doesn't arrive, share this link directly. It expires {new Date(result.claimExpiresAt).toLocaleString()}.
            </p>
            <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', marginBottom: 16 }}>
              {result.claimUrl}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={copyClaimURL}>Copy link</button>
              <button class="primary" onClick={onCreated}>Done</button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <h2 style={{ marginTop: 0 }}>New Organization</h2>
            <p style={{ color: 'var(--admin-text-dim)', fontSize: 13 }}>
              Creates a tenant org and emails the owner a one-time setup link to choose a password and enter Studio.
            </p>
            {err && <div class="error-banner">{err}</div>}
            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Organization name</span>
              <input
                value={name}
                onInput={(e) => {
                  const v = (e.target as HTMLInputElement).value;
                  setName(v);
                  if (!slugTouched) setSlug(deriveSlug(v));
                }}
                placeholder="Charlie Yost Photography"
                required
                style={{ width: '100%' }}
              />
            </label>
            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>URL slug</span>
              <input
                value={slug}
                onInput={(e) => {
                  setSlugTouched(true);
                  setSlug((e.target as HTMLInputElement).value);
                }}
                placeholder="charlie-yost"
                pattern="[a-z0-9](?:[a-z0-9-]*[a-z0-9])?"
                required
                style={{ width: '100%', fontFamily: 'monospace' }}
              />
              <span style={{ fontSize: 12, color: 'var(--admin-text-dim)' }}>
                Lowercase, dashes only. Used in public gallery URLs (e.g. /p/{slug || 'your-slug'}-XXXX).
              </span>
            </label>
            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Owner name</span>
              <input
                value={ownerName}
                onInput={(e) => setOwnerName((e.target as HTMLInputElement).value)}
                placeholder="Charlie Yost"
                required
                style={{ width: '100%' }}
              />
            </label>
            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Owner email</span>
              <input
                type="email"
                value={ownerEmail}
                onInput={(e) => setOwnerEmail((e.target as HTMLInputElement).value)}
                placeholder="charlie@example.com"
                required
                style={{ width: '100%' }}
              />
              <span style={{ fontSize: 12, color: 'var(--admin-text-dim)' }}>
                Receives the one-time claim link. Becomes the photographer login email.
              </span>
            </label>
            <label style={{ display: 'block', marginBottom: 16 }}>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Welcome note <span style={{ color: 'var(--admin-text-dim)' }}>(optional)</span></span>
              <textarea
                value={welcomeNote}
                onInput={(e) => setWelcomeNote((e.target as HTMLTextAreaElement).value)}
                rows={3}
                maxLength={1000}
                placeholder="Welcome to MomentPix. Reach out if you need anything."
                style={{ width: '100%', resize: 'vertical' }}
              />
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose} disabled={submitting}>Cancel</button>
              <button type="submit" class="primary" disabled={submitting}>
                {submitting ? 'Creating…' : 'Create organization'}
              </button>
            </div>
          </form>
        )}
      </div>
      {stepUpRetry && (
        <StepUpModal
          prompt="Creating an organization is a sensitive action and requires re-entering your password."
          onSuccess={() => {
            const retry = stepUpRetry;
            setStepUpRetry(null);
            retry();
          }}
          onCancel={() => setStepUpRetry(null)}
        />
      )}
    </div>
  );
}
